/**
 * @description 跳转到画布的 Hook，封装了构建画布链接和导航的逻辑
 */

import {useCallback} from 'react';
import {useNavigate} from 'react-router-dom';
import {useProjectId} from '@/hooks/useProjectId';
import {CanvasType} from '@/constants/canvas';
import type {CanvasTypeValue, GoCanvasParams} from '@/types/canvas';
import {openInNewTab} from '@/utils/url';

type JumpMode = 'normal' | 'blank';

// 画布页入口（用于画布页展现打点的 from 字段）
export const CanvasFrom = {
    // 工作台页面
    WORKSPACE: 'workspace',
    // 资产页面
    ASSETS: 'subject_asset',
    // 分集页面
    EPISODE: 'storyboard_table',
};

export type CanvasFromValue = (typeof CanvasFrom)[keyof typeof CanvasFrom];

export const useGoCanvas = <T extends CanvasTypeValue>() => {
    const navigate = useNavigate();
    const projectId = useProjectId();

    return useCallback(
        (
            params: GoCanvasParams<T>,
            options?: {
                mode?: JumpMode;
            },
        ) => {
            if (!projectId) return;

            const mode = options?.mode ?? 'blank';

            // 必传参数
            const queryParams = new URLSearchParams({
                projectId,
                canvasType: String(params.canvasType),
            });
            if (params.from) {
                queryParams.set('from', params.from);
            }

            // 画风测试
            if (params.canvasType === CanvasType.STYLE) {
                const {styleId, styleType, ratio} = params;
                const sourceExt = {
                    ...(styleId !== undefined && {styleId}),
                    ...(styleType !== undefined && {styleType}),
                    ...(ratio !== undefined && {ratio}),
                };
                queryParams.set('sourceExt', JSON.stringify(sourceExt));

                // 分镜&分集
            } else if (params.canvasType === CanvasType.EPISODE) {
                const {storyboardId, storyboardMaterialType, episodeId} = params;
                const sourceExt = {
                    ...(storyboardId !== undefined && {storyboardId}),
                    ...(storyboardMaterialType !== undefined && {storyboardMaterialType}),
                };
                queryParams.set('entryId', episodeId.toString());
                queryParams.set('sourceExt', JSON.stringify(sourceExt));

                // 资产
            } else if (params.canvasType === CanvasType.ASSETS) {
                const {assetId, assetType} = params;
                const sourceExt = {
                    ...(assetType !== undefined && {assetType}),
                    ...(assetId !== undefined && {assetId}),
                };
                queryParams.set('sourceExt', JSON.stringify(sourceExt));
            }

            const jumpUrl = `/canvas?${queryParams.toString()}`;

            if (mode === 'blank') {
                openInNewTab(jumpUrl);
            } else {
                void navigate(jumpUrl);
            }
        },
        [navigate, projectId],
    );
};
